import type {
  FormattedMemberPromotion,
  FormattedMemberPromotions,
  FormattedMemberPromotionsResponse,
  MemberPromotionAPIRequest,
  MemberPromotionAPIResponse,
  MemberPromotionsAPIRequest,
  MemberPromotionsAPIResponse
} from '@library/types/members/memberPromotions';
import { concat, mergeDeepWith } from 'ramda';
import {
  transformMemberPromotion,
  transformMemberPromotions,
  transformPromotionTypes
} from '@bo-utils/members/memberPromotions';
import type { ComboboxOption } from '@library/components/selects';
import type { GenericFilterTypesApiResponse } from '@library/types/utils';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { defaultHeaders } from '@bo-constants/apiHeaders';

const { member, memberPromotions } = apiPaths;

export const memberPromotionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMemberPromotions: build.query<
      FormattedMemberPromotionsResponse | undefined,
      MemberPromotionsAPIRequest
    >({
      query: ({ memberId, page, perPage, promotionType }) => ({
        url: `${member}/${memberId}/promotions`,
        method: 'GET',
        params: {
          page,
          per_page: perPage,
          type: promotionType
        },
        headers: defaultHeaders
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        return mergeDeepWith(
          (a: FormattedMemberPromotion, b: FormattedMemberPromotion) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return concat(a, b) as FormattedMemberPromotions;
            }
            return b;
          },
          currentCache,
          newItems
        ) as FormattedMemberPromotionsResponse;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg),
      transformResponse: (
        response: MemberPromotionsAPIResponse,
        _,
        { page, perPage }
      ) => {
        return transformMemberPromotions({ page, perPage, response });
      },
      providesTags: ['MemberReferrals']
    }),
    getMemberPromotion: build.query<
      FormattedMemberPromotion | undefined,
      MemberPromotionAPIRequest
    >({
      query: ({ memberId, promotionId }) => ({
        url: `${member}/${memberId}/promotions/${promotionId}`,
        method: 'GET',
        headers: defaultHeaders
      }),
      transformResponse: (response: MemberPromotionAPIResponse) => {
        return transformMemberPromotion({ response });
      },
      providesTags: ['MemberReferrals']
    }),
    getMemberPromotionTypes: build.query<Array<ComboboxOption>, void>({
      query: () => ({
        url: `${memberPromotions}/promotion_types`,
        method: 'GET',
        headers: defaultHeaders
      }),
      transformResponse: (
        response: GenericFilterTypesApiResponse | undefined
      ) => {
        return transformPromotionTypes({ response });
      }
    })
  })
});

export const {
  useGetMemberPromotionQuery,
  useGetMemberPromotionTypesQuery,
  useGetMemberPromotionsQuery
} = memberPromotionsApi;
